import { Button, Divider, Form, Input, InputNumber, Modal, Radio, Tag } from 'antd';
import FormField from 'components/FormField';
import { getStoreCount } from 'components/StoreScopes';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  AiModelState,
  getPrefixName,
  hideRadioClassName,
  defaultValueMap,
  Inline,
  Prefix,
  stores2storeScopes,
} from './share';
import { updateTransitDays } from 'views/BootGuide/service';
import useSmartField from 'hooks/useSmartField';
import { T, useTranslate } from '@tolgee/react';
import { useGetRegionLabel } from 'hooks/business/useRegion';
import styled from '@emotion/styled';
import NormalMode from './NormalMode';
import TransComponent from 'components/TransComponent';
import TagSelect from 'components/TagSelect';
import { PlusOutlined } from '@ant-design/icons';
import useModal from 'hooks/useModal';
import { Formik } from 'formik';
import { uniq } from 'lodash-es';

type FV = BootGuide.AiModel.FormValues.TransitDays & {
  customAreas?: string[];
};

export const regionAreasMap = {
  TH: ['TH_LOCAL_CENTER', 'TH_LOCAL_AROUND', 'TH_LOCAL_OUTSIDE'],
  PH: ['PH_LOCAL_CENTER', 'PH_LOCAL_OUTSIDE'],
  VN: ['VN_LOCAL_CENTER', 'VN_HANOI', 'VN_DA_NANG', 'VN_LOCAL_OUTSIDE'],
  MY: ['MY_LOCAL_CENTER', 'MY_LOCAL_OUTSIDE'],
  ID: ['ID_LOCAL_CENTER', 'ID_LOCAL_OUTSIDE'],
} as const;

export function useAreaLabel() {
  const { t } = useTranslate();

  const areaLabel = useMemo<Partial<Record<string, string>>>(() => {
    return {
      TH_LOCAL_CENTER: t('曼谷市区'),
      TH_LOCAL_AROUND: t('曼谷周边地区'),
      TH_LOCAL_OUTSIDE: t('曼谷以外地区'),
      PH_LOCAL_CENTER: t('马尼拉大都会内'),
      PH_LOCAL_OUTSIDE: t('马尼拉大都会外'),
      VN_LOCAL_CENTER: t('胡志明市'),
      VN_HANOI: t('HanoiCity'),
      VN_DA_NANG: t('岘港市'),
      VN_LOCAL_OUTSIDE: t('越南其余地区'),
      MY_LOCAL_CENTER: t('西马来西亚'),
      MY_LOCAL_OUTSIDE: t('东马来西亚'),
      ID_LOCAL_CENTER: t('爪哇岛内'),
      ID_LOCAL_OUTSIDE: t('爪哇岛外'),
    };
  }, [t]);

  const getAreaLabel = useCallback(
    (area: string) => {
      if (areaLabel[area]) {
        return areaLabel[area];
      }
      return undefined;
    },
    [areaLabel],
  );

  return {
    t,
    getAreaLabel,
  };
}

export function useTransportLabel() {
  const { t } = useTranslate();

  const transportLabel = useMemo(() => {
    return {
      LOCAL_SELLER: t('fulfillmentFlagValues.fulfilled_by_local_seller'),
      SHOPEE: t('fulfillmentFlagValues.fulfilled_by_shopee'),
      CROSS_BORDER_SELLER: t('fulfillmentFlagValues.fulfilled_by_cb_seller'),
    };
  }, [t]);

  const getTransportLabel = useCallback(
    (transport: BootGuide.AiModel.FormValues.TransportMethod) => {
      if (transportLabel[transport]) {
        return transportLabel[transport];
      }
      return undefined;
    },
    [transportLabel],
  );

  return {
    t,
    transportLabel,
    getTransportLabel,
  };
}

export const areaRegions: BootGuide.AiModel.AreaRegion[] = ['TH', 'PH', 'VN', 'MY', 'ID'];

const defaultStoreScopes: General.StoreScope[] = [];

function TransitDays({
  prefix,
  singleMode,
  storeMap,
}: {
  prefix?: Prefix;
  singleMode?: boolean;
  storeMap: AiModelState<FV>['storeMap'];
}) {
  const { getAreaLabel, t } = useAreaLabel();
  const { transportLabel } = useTransportLabel();

  const {
    field: { value: valueType, onChange: _onValueTypeChange },
  } = useSmartField<FV['valueType']>(getPrefixName('valueType', prefix));
  const onValueTypeChange = (valueType: FV['valueType']) => {
    _onValueTypeChange(valueType);
    setTimeout(() => {
      _onValueTypeChange(valueType);
    });
  };
  const {
    field: { value: storeScopes = defaultStoreScopes },
  } = useSmartField<FV['storeScopes']>(getPrefixName('storeScopes', prefix));
  const {
    field: { value: areasValue = [], onChange: _onAreasChange },
  } = useSmartField<FV['areas']>(getPrefixName('areas', prefix));
  const onAreasChange = (areas: FV['areas']) => {
    _onAreasChange(areas);
    setTimeout(() => {
      _onAreasChange(areas);
    });
  };

  const {
    field: { value: transports = [], onChange: onTransportsChange },
  } = useSmartField<FV['transports']>(getPrefixName('transports', prefix));

  const {
    field: { value: customAreas, onChange: onCustomAreasChange },
  } = useSmartField<FV['customAreas']>(getPrefixName('customAreas', prefix));

  const areas = useMemo(() => {
    return storeScopes.reduce<string[]>((res, { regionStoreScopes }) => {
      return regionStoreScopes.reduce((res, { storeIds }) => {
        return (
          storeIds?.reduce((res, id) => {
            const store = storeMap[id];
            if (!store) {
              return res;
            }
            const { region } = store;
            if (areaRegions.includes(region)) {
              regionAreasMap[region].forEach((area) => {
                if (!res.includes(area)) {
                  res.push(area);
                }
              });
            }
            return res;
          }, res) || res
        );
      }, res);
    }, []);
  }, [storeScopes, storeMap]);

  const refsCurrent = {
    areasValue,
    onAreasChange,
    valueType,
    onValueTypeChange,
  };
  const refs = useRef(refsCurrent);
  refs.current = refsCurrent;

  useEffect(() => {
    let needUpdate = false;
    const next = refs.current.areasValue.map((value) => {
      const nextAreas = value.areas.filter((_) => [...areas, ...(customAreas || [])].includes(_));
      if (nextAreas.length < value.areas.length) {
        needUpdate = true;
        return {
          ...value,
          areas: nextAreas,
        };
      }
      return value;
    });
    if (needUpdate) {
      refs.current.onAreasChange(next);
    }
  }, [areas, customAreas]);

  const { visible, show, hide, meta } = useModal<{
    onCreate: (customArea: string) => void;
  }>();

  const inputRef = useRef<Input>(null);

  useEffect(() => {
    if (visible) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 200);
    }
  }, [visible]);

  const customAreaModal = meta && (
    <Formik
      initialValues={{ value: '' }}
      onSubmit={(values) => {
        onCustomAreasChange(uniq([...(customAreas || []), values.value]));
        meta.onCreate(values.value);
        hide();
      }}
    >
      {({ handleSubmit, resetForm }) => {
        return (
          <Modal
            visible={visible}
            width={448}
            title={t('自定义地区')}
            onCancel={() => hide()}
            onOk={() => handleSubmit()}
            afterClose={() => {
              setTimeout(() => {
                resetForm();
              }, 200);
            }}
          >
            <Form layout="vertical">
              <FormField<string>
                name="value"
                required
                label={t('自定义地区')}
                validate={(value) => {
                  if (!value || !value.trim()) {
                    return t('请输入自定义地区名称\n');
                  }
                  if (customAreas?.includes(value) || areas.includes(value)) {
                    return t('地区已存在');
                  }
                }}
              >
                {({ field }) => {
                  return (
                    <Input
                      ref={inputRef}
                      placeholder={t('请输入自定义地区名称\n')}
                      onPressEnter={() => handleSubmit()}
                      {...field}
                    />
                  );
                }}
              </FormField>
            </Form>
          </Modal>
        );
      }}
    </Formik>
  );

  const getHideStyle = (type: FV['valueType']) =>
    singleMode && valueType !== type ? { display: 'none' } : undefined;

  const hasShopee = getStoreCount(storeScopes.filter(({ platform }) => platform === 'SHOPEE')) > 0;

  useEffect(() => {
    if (!hasShopee && refs.current.valueType === 'transport') {
      refs.current.onValueTypeChange('asSoon');
    }
  }, [hasShopee]);

  return (
    <div>
      <FormField<FV['range']>
        name={getPrefixName('range', prefix)}
        validate={({ min, max } = {} as any) =>
          ((!min && min !== 0) || (!max && max !== 0)) &&
          valueType === 'range' &&
          getStoreCount(storeScopes) > 0
            ? t('trade.请输入')
            : undefined
        }
        style={getHideStyle('range')}
      >
        {({ field: { value = {} as any, onChange, onBlur } }) => {
          return (
            <Radio
              className={singleMode ? hideRadioClassName : undefined}
              checked={valueType === 'range'}
              onChange={({ target: { checked } }) => checked && onValueTypeChange('range')}
            >
              <Inline onBlur={onBlur}>
                <T
                  keyName="transit_days_range"
                  params={{
                    x: (
                      <TransComponent
                        content={
                          <InputNumber
                            min={0}
                            max={value.max || value.max === 0 ? value.max : undefined}
                            precision={0}
                            value={value.min}
                            onChange={(min) =>
                              onChange({
                                ...value,
                                min: min as any,
                              })
                            }
                          />
                        }
                      />
                    ),
                    y: (
                      <TransComponent
                        content={
                          <InputNumber
                            min={value.min || 0}
                            precision={0}
                            value={value.max}
                            onChange={(max) =>
                              onChange({
                                ...value,
                                max: max as any,
                              })
                            }
                          />
                        }
                      />
                    ),
                  }}
                />
              </Inline>
            </Radio>
          );
        }}
      </FormField>
      {
        <div className="mb-4" style={getHideStyle('area')}>
          {!singleMode && (
            <Radio
              checked={valueType === 'area'}
              onChange={({ target: { checked } }) => checked && onValueTypeChange('area')}
            >
              {t('指定地区')}
            </Radio>
          )}
          {areasValue.map((_, index) => {
            const content = (
              <FormField<FV['areas'][0]>
                name={getPrefixName(`areas.${index}`, prefix)}
                validate={({ areas, rangeMin, rangeMax }) => {
                  if (valueType === 'area' && getStoreCount(storeScopes) > 0) {
                    if (!areas.length) {
                      return t('请选择');
                    }
                  }
                  if ((!rangeMin && rangeMin !== 0) || (!rangeMax && rangeMax !== 0)) {
                    return t('trade.请输入');
                  }
                }}
              >
                {({ field: { value, onChange, onBlur } }) => {
                  const onChangeAreas = (value: FV['areas'][0]['areas']) => {
                    onAreasChange(
                      areasValue.map(({ areas, ...others }, i) => {
                        if (i === index) {
                          return {
                            ...others,
                            areas: value,
                          };
                        }
                        return {
                          ...others,
                          areas: areas.filter((_) => !value.includes(_)),
                        };
                      }),
                    );
                  };
                  return (
                    <AreaInline onBlur={onBlur}>
                      <TagSelect
                        showArrow
                        mode="multiple"
                        placeholder={t('请选择')}
                        dropdownMatchSelectWidth={false}
                        value={value.areas}
                        onChange={onChangeAreas}
                        options={[
                          ...areas.map((area) => {
                            return {
                              value: area,
                              label: getAreaLabel(area),
                            };
                          }),
                          ...(customAreas || []).map((value) => ({ value, label: value })),
                        ]}
                        dropdownRender={(menu) => (
                          <>
                            {menu}
                            <Divider style={{ margin: '8px 0' }} />
                            <Button
                              block
                              type="link"
                              icon={<PlusOutlined />}
                              onClick={() =>
                                show({
                                  onCreate: (customArea) =>
                                    onChangeAreas([...value.areas, customArea]),
                                })
                              }
                            >
                              {t('自定义地区')}
                            </Button>
                          </>
                        )}
                      />
                      <T
                        keyName="transit_days_range"
                        params={{
                          x: (
                            <TransComponent
                              content={
                                <InputNumber
                                  min={0}
                                  max={
                                    value.rangeMax || value.rangeMax === 0
                                      ? value.rangeMax
                                      : undefined
                                  }
                                  precision={0}
                                  value={value.rangeMin}
                                  onChange={(rangeMin) =>
                                    onChange({
                                      ...value,
                                      rangeMin: rangeMin as any,
                                    })
                                  }
                                />
                              }
                            />
                          ),
                          y: (
                            <TransComponent
                              content={
                                <InputNumber
                                  min={value.rangeMin || 0}
                                  precision={0}
                                  value={value.rangeMax}
                                  onChange={(rangeMax) =>
                                    onChange({
                                      ...value,
                                      rangeMax: rangeMax as any,
                                    })
                                  }
                                />
                              }
                            />
                          ),
                        }}
                      />
                    </AreaInline>
                  );
                }}
              </FormField>
            );
            const action =
              index === 0 ? (
                <Button
                  type="link"
                  onClick={() => {
                    const unConfigAreas = areas.filter((area) => {
                      return areasValue.every(({ areas }) => {
                        return !areas.includes(area);
                      });
                    });
                    onAreasChange([
                      ...areasValue,
                      {
                        areas: unConfigAreas,
                        rangeMin: 3,
                        rangeMax: 10,
                      },
                    ]);
                  }}
                >
                  {t('添加')}
                </Button>
              ) : (
                <Button
                  type="link"
                  onClick={() =>
                    onAreasChange([...areasValue.slice(0, index), ...areasValue.slice(index + 1)])
                  }
                >
                  {t('marketing.删除')}
                </Button>
              );
            if (singleMode) {
              return content;
            }
            return (
              <AreaContainer>
                {content}
                {action}
              </AreaContainer>
            );
          })}
        </div>
      }
      {hasShopee && (
        <FormField<FV['transports']>
          name={getPrefixName('transports', prefix)}
          style={getHideStyle('transport')}
          help={
            valueType === 'transport' &&
            getStoreCount(storeScopes.filter(({ platform }) => platform !== 'SHOPEE')) > 0 ? (
              <span className="ant-form-item-explain-error">
                {t('该配置仅对Shopee平台生效，请为其他平台店铺重新配置运输天数')}
              </span>
            ) : undefined
          }
        >
          {!singleMode && (
            <Radio
              checked={valueType === 'transport'}
              onChange={({ target: { checked } }) => checked && onValueTypeChange('transport')}
            >
              {t('指定发货方式(Shopee)')}
            </Radio>
          )}
          {transports.map((_, index) => {
            const content = (
              <FormField<FV['transports'][0]>
                name={getPrefixName(`transports.${index}`, prefix)}
                validate={({ deliveryMethods, rangeMin, rangeMax }) => {
                  if (valueType === 'transport' && getStoreCount(storeScopes) > 0) {
                    if (!deliveryMethods.length) {
                      return t('请选择');
                    }
                  }
                  if ((!rangeMin && rangeMin !== 0) || (!rangeMax && rangeMax !== 0)) {
                    return t('trade.请输入');
                  }
                }}
              >
                {({ field: { value, onChange, onBlur } }) => {
                  const onChangeTransports = (value: FV['transports'][0]['deliveryMethods']) => {
                    onTransportsChange(
                      transports.map(({ deliveryMethods, ...others }, i) => {
                        if (i === index) {
                          return {
                            ...others,
                            deliveryMethods: value,
                          };
                        }
                        return {
                          ...others,
                          deliveryMethods: deliveryMethods.filter((_) => !value.includes(_)),
                        };
                      }),
                    );
                  };
                  return (
                    <AreaInline onBlur={onBlur}>
                      <TagSelect
                        showArrow
                        mode="multiple"
                        placeholder={t('请选择')}
                        dropdownMatchSelectWidth={false}
                        value={value.deliveryMethods}
                        onChange={onChangeTransports}
                        options={Object.entries(transportLabel).map(([value, label]) => {
                          return {
                            value,
                            label,
                          };
                        })}
                      />
                      <T
                        keyName="transit_days_range"
                        params={{
                          x: (
                            <TransComponent
                              content={
                                <InputNumber
                                  min={0}
                                  max={
                                    value.rangeMax || value.rangeMax === 0
                                      ? value.rangeMax
                                      : undefined
                                  }
                                  precision={0}
                                  value={value.rangeMin}
                                  onChange={(rangeMin) =>
                                    onChange({
                                      ...value,
                                      rangeMin: rangeMin as any,
                                    })
                                  }
                                />
                              }
                            />
                          ),
                          y: (
                            <TransComponent
                              content={
                                <InputNumber
                                  min={value.rangeMin || 0}
                                  precision={0}
                                  value={value.rangeMax}
                                  onChange={(rangeMax) =>
                                    onChange({
                                      ...value,
                                      rangeMax: rangeMax as any,
                                    })
                                  }
                                />
                              }
                            />
                          ),
                        }}
                      />
                    </AreaInline>
                  );
                }}
              </FormField>
            );
            if (singleMode) {
              return content;
            }
            const action =
              index === 0 ? (
                <Button
                  type="link"
                  onClick={() => {
                    const unConfigMethods = Object.keys(transportLabel).filter((method) => {
                      return transports.every(({ deliveryMethods }) => {
                        return !deliveryMethods.includes(method);
                      });
                    });
                    onTransportsChange([
                      ...transports,
                      {
                        deliveryMethods: unConfigMethods,
                        rangeMin: 3,
                        rangeMax: 10,
                      },
                    ]);
                  }}
                >
                  {t('添加')}
                </Button>
              ) : (
                <Button
                  type="link"
                  onClick={() =>
                    onTransportsChange([
                      ...transports.slice(0, index),
                      ...transports.slice(index + 1),
                    ])
                  }
                >
                  {t('marketing.删除')}
                </Button>
              );
            return (
              <AreaContainer>
                {content}
                {action}
              </AreaContainer>
            );
          })}
        </FormField>
      )}
      <Radio
        className={singleMode ? hideRadioClassName : undefined}
        style={getHideStyle('asSoon')}
        checked={valueType === 'asSoon'}
        onChange={({ target: { checked } }) => checked && onValueTypeChange('asSoon')}
      >
        {t('会尽快运输')}
      </Radio>
      {customAreaModal}
    </div>
  );
}

const defaultValues = defaultValueMap.AI_MODEL_TRANSIT_DAYS;

export function TransitDaysBootGuide(props: AiModelState<FV>) {
  return (
    <NormalMode
      state={props}
      tips={props.t('您的店铺商品运输天数是多久呢？以下是一些常见的运输天数选项供您选择和调整')}
      getFormContent={(prefix) => <TransitDays prefix={prefix} storeMap={props.storeMap} />}
      getFirstInitialValues={useCallback(({ stores, defaultValueRule }) => {
        if (!defaultValueRule) {
          return { values: [] };
        }

        const {
          LOGISTICS_DELIVER: {
            RANGE: { transportRanges },
            DESIGNATE_AREA: { areaRanges },
          },
        } = defaultValueRule;

        const values: FV[] = [];

        const areas = stores.reduce<string[]>((res, store) => {
          const { is3pf, storeType, region } = store;
          if ((is3pf || storeType === 'LOCAL') && areaRegions.includes(region)) {
            regionAreasMap[region].forEach((area) => {
              if (!res.indexOf(area)) {
                res.push(area);
              }
            });
          }
          return res;
        }, []);

        if (areas.length) {
          const targetStores = stores.filter(
            ({ is3pf, storeType, region }) =>
              (is3pf || storeType === 'LOCAL') && areaRegions.includes(region),
          );

          if (targetStores.length) {
            values.push({
              ...defaultValues,
              valueType: 'area',
              storeScopes: stores2storeScopes(targetStores),
              areas:
                areaRanges
                  .find(({ storeType }) => storeType === 'LOCAL')
                  ?.areaRange.map((item) => {
                    return {
                      areas: item.areas.filter((_) => areas.includes(_)),
                      rangeMin: item.min,
                      rangeMax: item.max,
                    };
                  })
                  .filter((_) => _.areas.length) || [],
            });
          }
        }

        transportRanges.forEach(({ storeType, regions, min, max }) => {
          const targetStores = stores.filter((store) => {
            if (
              (store.is3pf || store.storeType === 'LOCAL') &&
              areaRegions.includes(store.region)
            ) {
              return false;
            }
            if (!regions.includes(store.region)) {
              return false;
            }
            if (storeType === 'LOCAL') {
              return store.storeType === 'LOCAL' || store.is3pf;
            }
            if (storeType === 'CROSS') {
              return store.storeType === 'CROSS' && !store.is3pf;
            }
            return false;
          });
          if (targetStores.length) {
            values.push({
              ...defaultValues,
              valueType: 'range',
              storeScopes: stores2storeScopes(targetStores),
              range: {
                min,
                max,
              },
            });
          }
        });

        return { values };
      }, [])}
      updateApi={updateTransitDays}
      getRemainTips={(remainLength) => props.t('您有x家店铺还未配置', { x: remainLength })}
      descriptionRender={(value, storeMap) => (
        <StoreDescription storeScopes={value} storeMap={storeMap} />
      )}
    />
  );
}

function StoreDescription({
  storeScopes,
  storeMap,
}: {
  storeScopes: General.StoreScope[];
  storeMap: AiModelState<FV>['storeMap'];
}) {
  const { t } = useTranslate();
  const getRegionLabel = useGetRegionLabel();

  const storeCounts = useMemo(() => {
    return storeScopes.reduce<
      Partial<Record<'local' | 'cross' | 'pf', Partial<Record<General.UpperCaseRegion, number>>>>
    >((res, { regionStoreScopes }) => {
      return regionStoreScopes.reduce((res, { region, storeIds }) => {
        if (region === 'all') {
          return res;
        }
        return (
          storeIds?.reduce((res, id) => {
            const store = storeMap[id];
            if (!store) {
              return res;
            }
            if (store.is3pf) {
              if (res.pf) {
                res.pf[region] = (res.pf[region] || 0) + 1;
              } else {
                res.pf = {
                  [region]: 1,
                };
              }
            } else if (store.storeType === 'LOCAL') {
              if (res.local) {
                res.local[region] = (res.local[region] || 0) + 1;
              } else {
                res.local = {
                  [region]: 1,
                };
              }
            } else if (store.storeType === 'CROSS') {
              if (res.cross) {
                res.cross[region] = (res.cross[region] || 0) + 1;
              } else {
                res.cross = {
                  [region]: 1,
                };
              }
            }
            return res;
          }, res) || res
        );
      }, res);
    }, {});
  }, [storeScopes, storeMap]);

  const tags = useMemo(() => {
    return Object.keys(storeCounts).map((key) => {
      const regionCountsMap = storeCounts[key] as Required<
        typeof storeCounts
      >[keyof typeof storeCounts];
      const count = Object.values(regionCountsMap).reduce((res, cur) => res + cur, 0);
      const color = {
        local: 'green',
        cross: 'blue',
        pf: 'orange',
      }[key];
      return (
        <Tag color={color}>
          {
            {
              local: t('本土店铺'),
              cross: t('跨境店铺'),
              pf: t('3PF店铺'),
            }[key]
          }
          ({count}) (
          {Object.keys(regionCountsMap)
            .map((region) => {
              return getRegionLabel(region);
            })
            .join(' | ')}
          )
        </Tag>
      );
    });
  }, [storeCounts, t, getRegionLabel]);

  return <>{tags}</>;
}

const AreaContainer = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 6px 8px;
  background: #f7f9fa;
  > div {
    flex: 1 0 0;
    overflow: hidden;
  }
  & + & {
    margin-top: 8px;
  }
`;

const AreaInline = styled(Inline)`
  .ant-select {
    width: auto !important;
    min-width: 200px;
    max-width: 400px;
  }
`;

export default TransitDays;
